import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { ALLOW_IN_MVP, SETTINGS_KEYS, environment } from '../../config/environment'
import { ModuleKey, MODULE_DEFAULT_ENABLED } from '../../constants/module-keys'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { useSetting } from '../../hooks/useSettings'
import { mailAccountService } from '../../services/mail-account.service'
import { mailRuleService } from '../../services/mail-rule.service'
import { PermissionAction, PermissionType, permissionsService } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { MailAccount, MailAccountType, MailRule } from '../../types'
import { useModuleFlag } from '../../utils/featureFlags'
import { ConfirmDialog } from '../common/ConfirmDialog'
import IfPermissions from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import { Pagination } from '../common/Pagination'
import Phase2NoticeModal from '../common/Phase2NoticeModal'
import './EmailSettings.scss'
import { MailAccountEditDialog } from './MailAccountEditDialog'
import { MailRuleEditDialog } from './MailRuleEditDialog'
import PermissionsDialog from './PermissionsDialog'
import ProcessedMailDialog from './ProcessedMailDialog'

enum EmailSettingsTab {
  Accounts = 'accounts',
  Rules = 'rules',
}

export const EmailSettings: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const canDisplayAccounts = permissionsService.currentUserCan(PermissionAction.Display, PermissionType.MailAccount)
  const canDisplayRules = permissionsService.currentUserCan(PermissionAction.Display, PermissionType.MailRule)
  const firstVisibleEmailTab: EmailSettingsTab = canDisplayAccounts
    ? EmailSettingsTab.Accounts
    : canDisplayRules
      ? EmailSettingsTab.Rules
      : EmailSettingsTab.Accounts

  const [activeTab, setActiveTab] = useState<EmailSettingsTab>(firstVisibleEmailTab)

  useEffect(() => {
    const stillVisible =
      (activeTab === EmailSettingsTab.Accounts && canDisplayAccounts) ||
      (activeTab === EmailSettingsTab.Rules && canDisplayRules)
    if (!stillVisible) {
      setActiveTab(firstVisibleEmailTab)
    }
  }, [activeTab, canDisplayAccounts, canDisplayRules, firstVisibleEmailTab])
  const [mailAccounts, setMailAccounts] = useState<MailAccount[]>([])
  const [mailRules, setMailRules] = useState<MailRule[]>([])
  const [accountsCount, setAccountsCount] = useState(0)
  const [rulesCount, setRulesCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [initialLoad, setInitialLoad] = useState(true)

  const [accountsPage, setAccountsPage] = useState(1)
  const [rulesPage, setRulesPage] = useState(1)
  const [accountsSortField, setAccountsSortField] = useState<string>('')
  const [rulesSortField, setRulesSortField] = useState<string>('')
  const [accountsSortReverse, setAccountsSortReverse] = useState(false)
  const [rulesSortReverse, setRulesSortReverse] = useState(false)

  const [showAccountDialog, setShowAccountDialog] = useState(false)
  const [showRuleDialog, setShowRuleDialog] = useState(false)
  const [showProcessedMailDialog, setShowProcessedMailDialog] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [editingAccount, setEditingAccount] = useState<MailAccount | undefined>()
  const [editingRule, setEditingRule] = useState<MailRule | undefined>()
  const [selectedRule, setSelectedRule] = useState<MailRule | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('')
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [selectedObject, setSelectedObject] = useState<MailAccount | MailRule | null>(null)
  const [gmailOAuthUrl, setGmailOAuthUrl] = useState<string | undefined>(undefined)
  const [outlookOAuthUrl, setOutlookOAuthUrl] = useState<string | undefined>(undefined)
  const [showTotpPhase2Modal, setShowTotpPhase2Modal] = useState(false)
  const isEmailIntegrationEnabled = useModuleFlag(
    ModuleKey.EmailIntegration,
    MODULE_DEFAULT_ENABLED[ModuleKey.EmailIntegration]
  )

  const canMutateMailAccount = (account: MailAccount) =>
    permissionsService.currentUserCan(PermissionAction.Change, PermissionType.MailAccount) &&
    permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, account)

  const canMutateMailRule = (rule: MailRule) =>
    permissionsService.currentUserCan(PermissionAction.Change, PermissionType.MailRule) &&
    permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, rule)

  const handleOAuthRedirect = useCallback((oauthUrl: string | undefined) => {
    if (ALLOW_IN_MVP) {
      setShowTotpPhase2Modal(true)
    } else {
      if (!oauthUrl) return
      window.location.href = oauthUrl
    }
  }, [])
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)

  useEffect(() => {
    const loadOAuthUrls = async () => {
      try {
        await settingsService.initializeSettings()
        const allSettings = (settingsService as any).settings || {}
        const gmailUrl = allSettings.gmail_oauth_url || settingsService.get('gmail_oauth_url') || settingsService.get(SETTINGS_KEYS.GMAIL_OAUTH_URL)
        const outlookUrl =
          allSettings.outlook_oauth_url || settingsService.get('outlook_oauth_url') || settingsService.get(SETTINGS_KEYS.OUTLOOK_OAUTH_URL)
        setGmailOAuthUrl(gmailUrl)
        setOutlookOAuthUrl(outlookUrl)
      } catch (error) {
        console.error('Error loading OAuth URLs:', error)
      }
    }

    loadOAuthUrls()
    const unsubscribe = settingsService.subscribe(() => {
      loadOAuthUrls()
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    document.title = `${t('mail_settings')} - ${appTitle}`
  }, [t, appTitle])

  const loadMailAccounts = useCallback(async () => {
    if (!isEmailIntegrationEnabled) {
      setMailAccounts([])
      setAccountsCount(0)
      return []
    }
    try {
      const response = await mailAccountService.listFiltered(accountsPage, undefined, accountsSortField, accountsSortReverse, undefined, false, {
        page_size: DEFAULT_PAGE_SIZE,
        full_perms: true,
      })
      setMailAccounts(response.results)
      setAccountsCount(response.count)
      return response.results
    } catch (err: any) {
      toastService.showError(t('error_loading_mail_accounts'))
      return []
    }
  }, [accountsPage, accountsSortField, accountsSortReverse, t, isEmailIntegrationEnabled])

  const loadMailRules = useCallback(async () => {
    if (!isEmailIntegrationEnabled) {
      setMailRules([])
      setRulesCount(0)
      return
    }
    try {
      const response = await mailRuleService.listFiltered(rulesPage, undefined, rulesSortField, rulesSortReverse, undefined, false, {
        page_size: DEFAULT_PAGE_SIZE,
        full_perms: true,
      })
      setMailRules(response.results)
      setRulesCount(response.count)
    } catch (err: any) {
      toastService.showError(t('error_loading_mail_rules'))
    }
  }, [rulesPage, rulesSortField, rulesSortReverse, t, isEmailIntegrationEnabled])

  useEffect(() => {
    const loadData = async () => {
      if (!isEmailIntegrationEnabled) {
        setLoading(false)
        setError(null)
        setInitialLoad(false)
        setMailAccounts([])
        setMailRules([])
        setAccountsCount(0)
        setRulesCount(0)
        return
      }
      try {
        setLoading(true)
        setError(null)
        if (activeTab === EmailSettingsTab.Accounts) {
          await loadMailAccounts()
        } else {
          await loadMailRules()
        }
        setInitialLoad(false)
      } catch (err) {
        setError(t('error_loading_mail_settings'))
        toastService.showError(t('error_loading_mail_settings'))
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [
    activeTab,
    accountsPage,
    accountsSortField,
    accountsSortReverse,
    rulesPage,
    rulesSortField,
    rulesSortReverse,
    loadMailAccounts,
    loadMailRules,
    t,
    isEmailIntegrationEnabled,
  ])

  const handleAccountsSort = useCallback(
    (column: string) => {
      setAccountsSortField((prevField) => {
        if (prevField !== column) {
          setAccountsSortReverse(false)
          return column
        } else if (prevField === column && !accountsSortReverse) {
          setAccountsSortReverse(true)
          return prevField
        } else {
          setAccountsSortReverse(false)
          return ''
        }
      })
    },
    [accountsSortReverse]
  )

  const handleRulesSort = useCallback(
    (column: string) => {
      setRulesSortField((prevField) => {
        if (prevField !== column) {
          setRulesSortReverse(false)
          return column
        } else if (prevField === column && !rulesSortReverse) {
          setRulesSortReverse(true)
          return prevField
        } else {
          setRulesSortReverse(false)
          return ''
        }
      })
    },
    [rulesSortReverse]
  )

  const getAccountIcon = (accountType: MailAccountType) => {
    switch (accountType) {
      case MailAccountType.IMAP:
        return 'bi-envelope-at-fill'
      case MailAccountType.Gmail_OAuth:
        return 'bi-google'
      case MailAccountType.Outlook_OAuth:
        return 'bi-microsoft'
      default:
        return 'bi-envelope'
    }
  }

  const handleEditAccount = (account?: MailAccount) => {
    setEditingAccount(account)
    setShowAccountDialog(true)
  }

  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success')
    const accountId = searchParams.get('account_id')

    if (oauthSuccess !== null) {
      if (oauthSuccess === '1') {
        toastService.showSuccess(t('oauth2_authentication_success') || 'OAuth2 autentifikacija uspešna')
        if (accountId) {
          const accountIdNum = parseInt(accountId, 10)
          if (!isNaN(accountIdNum)) {
            loadMailAccounts().then((accounts) => {
              const account = accounts.find((acc) => acc.id === accountIdNum)
              if (account) {
                handleEditAccount(account)
              } else {
                setTimeout(() => {
                  loadMailAccounts().then((accountsAfterRetry) => {
                    const accountAfterRetry = accountsAfterRetry.find((acc) => acc.id === accountIdNum)
                    if (accountAfterRetry) {
                      handleEditAccount(accountAfterRetry)
                    }
                  })
                }, 200)
              }
            })
          }
        }
      } else {
        toastService.showError(t('oauth2_authentication_failed') || 'OAuth2 autentifikacija neuspešna, proverite logove')
      }

      searchParams.delete('oauth_success')
      searchParams.delete('account_id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, t, loadMailAccounts, handleEditAccount])

  const handleDeleteAccount = async (account: MailAccount) => {
    const executeDelete = async () => {
      try {
        await mailAccountService.deleteMailAccount(account.id)
        toastService.showSuccess(t('mail_account_deleted', { name: account.name }))
        await loadMailAccounts()
        setShowConfirmDialog(false)
      } catch (error) {
        toastService.showError(t('error_deleting_mail_account', { name: account.name }))
      }
    }

    setConfirmDialogMessage(
      t('confirm_delete_mail_account', { name: account.name }) || `Da li zaista želite da obrišete mail nalog "${account.name}"?`
    )
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleProcessAccount = async (account: MailAccount) => {
    try {
      await mailAccountService.processMailAccount(account.id)
      toastService.showSuccess(t('mail_account_processed', { name: account.name }))
    } catch (error) {
      toastService.showError(t('error_processing_mail_account', { name: account.name }))
    }
  }

  const handleEditRule = (rule?: MailRule) => {
    setEditingRule(rule)
    setShowRuleDialog(true)
  }

  const handleDeleteRule = async (rule: MailRule) => {
    const executeDelete = async () => {
      try {
        await mailRuleService.deleteMailRule(rule.id)
        toastService.showSuccess(t('mail_rule_deleted', { name: rule.name }))
        await loadMailRules()
        setShowConfirmDialog(false)
      } catch (error) {
        toastService.showError(t('error_deleting_mail_rule', { name: rule.name }))
      }
    }

    setConfirmDialogMessage(t('confirm_delete_mail_rule', { name: rule.name }) || `Da li zaista želite da obrišete mail pravilo "${rule.name}"?`)
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleCopyRule = async (rule: MailRule) => {
    try {
      const copyData = { ...rule }
      delete (copyData as any).id
      copyData.name = `${rule.name} (Copy)`
      await mailRuleService.createMailRule(copyData)
      toastService.showSuccess(t('mail_rule_copied', { name: rule.name }))
      await loadMailRules()
    } catch (error) {
      toastService.showError(t('error_copying_mail_rule', { name: rule.name }))
    }
  }

  const handleViewProcessedMail = (rule: MailRule) => {
    setSelectedRule(rule)
    setShowProcessedMailDialog(true)
  }

  const handleProcessedMailDialogClose = () => {
    setShowProcessedMailDialog(false)
    setSelectedRule(null)
  }

  const handleEditPermissions = (object: MailAccount | MailRule) => {
    setSelectedObject(object)
    setShowPermissionsDialog(true)
  }

  const handlePermissionsSave = async (permissions: any) => {
    if (!selectedObject) return

    try {
      if ('account' in selectedObject) {
        await mailRuleService.update(selectedObject.id, {
          ...selectedObject,
          owner: permissions.owner,
          set_permissions: permissions.set_permissions,
        })
      } else {
        await mailAccountService.update(selectedObject.id, {
          ...selectedObject,
          owner: permissions.owner,
          set_permissions: permissions.set_permissions,
        })
      }

      await loadMailAccounts()
      await loadMailRules()
    } catch (error) {
      throw error
    }
  }

  const handleAccountDialogClose = () => {
    setShowAccountDialog(false)
    setEditingAccount(undefined)
  }

  const handleRuleDialogClose = () => {
    setShowRuleDialog(false)
    setEditingRule(undefined)
  }

  const handleAccountSave = () => {
    loadMailAccounts()
  }

  const handleRuleSave = () => {
    loadMailRules()
  }

  const handleToggleRuleEnabled = async (rule: MailRule) => {
    try {
      await mailRuleService.updateMailRule(rule.id, { enabled: !rule.enabled })
      toastService.showSuccess(t('mail_rule_updated', { name: rule.name }))
      await loadMailRules()
    } catch (error) {
      toastService.showError(t('error_updating_mail_rule', { name: rule.name }))
    }
  }

  const getAccountName = (accountId: number | null) => {
    if (!accountId) return t('no_account_selected')
    const account = mailAccounts.find((acc) => acc.id === accountId)
    return account?.name || `Account ${accountId}`
  }

  const accountsTotalPages = Math.max(1, Math.ceil(accountsCount / DEFAULT_PAGE_SIZE))
  const rulesTotalPages = Math.max(1, Math.ceil(rulesCount / DEFAULT_PAGE_SIZE))
  const accountsRangeStart = accountsCount === 0 ? 0 : (accountsPage - 1) * DEFAULT_PAGE_SIZE + 1
  const accountsRangeEnd = Math.min(accountsCount, accountsPage * DEFAULT_PAGE_SIZE)
  const rulesRangeStart = rulesCount === 0 ? 0 : (rulesPage - 1) * DEFAULT_PAGE_SIZE + 1
  const rulesRangeEnd = Math.min(rulesCount, rulesPage * DEFAULT_PAGE_SIZE)

  if (loading && initialLoad) {
    return (
      <div className="email-settings">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
          <p className="mt-2">{t('loading_mail_settings')}</p>
        </div>
      </div>
    )
  }

  if (!isEmailIntegrationEnabled) {
    return (
      <div className="email-settings">
        <PageHeader title={t('mail_settings')} info={t('mail_settings_info')} infoLink="mail" />
        <Alert variant="warning" className="mt-3">
          <i className="bi bi-info-circle me-2"></i>
          {t('email_integration_disabled_message')}
        </Alert>
      </div>
    )
  }

  if (error) {
    return (
      <div className="email-settings">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="email-settings">
      <PageHeader title={t('mail_settings')} info={t('mail_settings_info')} infoLink="mail" />

      <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k as EmailSettingsTab)} className="mb-3">
        {canDisplayAccounts && (
        <Tab eventKey={EmailSettingsTab.Accounts} title={t('mail_accounts')}>
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{t('mail_accounts')}</h4>
              <div className="d-flex gap-2">
                <IfPermissions action={PermissionAction.Add} type={PermissionType.MailAccount}>
                  <Button variant="primary" size="sm" onClick={() => handleEditAccount()}>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('add_account')}
                  </Button>
                </IfPermissions>
                <IfPermissions action={PermissionAction.Add} type={PermissionType.MailAccount}>
                  <Button
                    variant={outlookOAuthUrl ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => handleOAuthRedirect(outlookOAuthUrl)}
                    disabled={!outlookOAuthUrl}
                    title={!outlookOAuthUrl ? t('oauth_not_configured_hint') : undefined}
                  >
                    <i className="bi bi-microsoft me-1"></i>
                    {t('connect_outlook_account')}
                  </Button>
                </IfPermissions>
                <IfPermissions action={PermissionAction.Add} type={PermissionType.MailAccount}>
                  <Button
                    variant={gmailOAuthUrl ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => handleOAuthRedirect(gmailOAuthUrl)}
                    disabled={!gmailOAuthUrl}
                    title={!gmailOAuthUrl ? t('oauth_not_configured_hint') : undefined}
                  >
                    <i className="bi bi-google me-1"></i>
                    {t('connect_gmail_account')}
                  </Button>
                </IfPermissions>
              </div>
            </div>

            {accountsTotalPages > 1 && (
              <div className="row mb-3">
                <div className="col-auto ms-auto">
                  <Pagination currentPage={accountsPage} totalPages={accountsTotalPages} onPageChange={setAccountsPage} alwaysVisible />
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleAccountsSort('name')}>
                      {t('name')}
                      {accountsSortField === 'name' && <i className={`bi bi-arrow-${accountsSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th className="sortable" onClick={() => handleAccountsSort('imap_server')}>
                      {t('server')}
                      {accountsSortField === 'imap_server' && <i className={`bi bi-arrow-${accountsSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th className="d-none d-sm-table-cell">{t('username')}</th>
                    <th className="text-end">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <Spinner size="sm" className="me-2" />
                        {t('loading')}...
                      </td>
                    </tr>
                  ) : mailAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('no_items_found')} {t('mail_accounts')}
                      </td>
                    </tr>
                  ) : (
                    mailAccounts.map((account) => {
                      return (
                        <tr key={account.id}>
                          <td>
                            <button className="btn btn-link p-0 text-start" onClick={() => handleEditAccount(account)}>
                              {account.name}
                              <i className={`bi ${getAccountIcon(account.account_type)} ms-2`}></i>
                            </button>
                          </td>
                          <td>{account.imap_server}</td>
                          <td className="d-none d-sm-table-cell">{account.username}</td>
                          <td className="text-end">
                            <div className="btn-group">
                              <Button variant="outline-secondary" size="sm" onClick={() => handleEditAccount(account)} title={t('edit')}>
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleEditPermissions(account)}
                                title={t('permissions')}
                              >
                                <i className="bi bi-person-lock"></i>
                              </Button>
                              <IfPermissions action={PermissionAction.Add} type={PermissionType.MailAccount}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleProcessAccount(account)}
                                  title={t('process_mail')}
                                >
                                  <i className="bi bi-arrow-clockwise"></i>
                                </Button>
                              </IfPermissions>
                              <IfPermissions action={PermissionAction.Delete} type={PermissionType.MailAccount}>
                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteAccount(account)} title={t('delete')}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </IfPermissions>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">{accountsCount > 0 ? `${accountsRangeStart} - ${accountsRangeEnd} / ${accountsCount}` : '0 / 0'}</div>
              <Pagination currentPage={accountsPage} totalPages={accountsTotalPages} onPageChange={setAccountsPage} alwaysVisible />
            </div>
          </div>
        </Tab>
        )}

        {canDisplayRules && (
        <Tab eventKey={EmailSettingsTab.Rules} title={t('mail_rules')}>
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{t('mail_rules')}</h4>
              <IfPermissions action={PermissionAction.Add} type={PermissionType.MailRule}>
                <Button variant="primary" size="sm" onClick={() => handleEditRule()}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_rule')}
                </Button>
              </IfPermissions>
            </div>

            {rulesTotalPages > 1 && (
              <div className="row mb-3">
                <div className="col-auto ms-auto">
                  <Pagination currentPage={rulesPage} totalPages={rulesTotalPages} onPageChange={setRulesPage} alwaysVisible />
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleRulesSort('name')}>
                      {t('name')}
                      {rulesSortField === 'name' && <i className={`bi bi-arrow-${rulesSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th className="sortable d-none d-sm-table-cell" onClick={() => handleRulesSort('order')}>
                      {t('sort_order')}
                      {rulesSortField === 'order' && <i className={`bi bi-arrow-${rulesSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th>{t('account')}</th>
                    <th className="d-none d-sm-table-cell">{t('status')}</th>
                    <th className="d-none d-md-table-cell">{t('processed_mail')}</th>
                    <th className="text-end">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <Spinner size="sm" className="me-2" />
                        {t('loading')}...
                      </td>
                    </tr>
                  ) : mailRules.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('no_items_found')} {t('mail_rules')}
                      </td>
                    </tr>
                  ) : (
                    mailRules.map((rule) => {
                      const canChangeRule = canMutateMailRule(rule)
                      return (
                        <tr key={rule.id}>
                          <td>
                            <button className="btn btn-link p-0 text-start" onClick={() => handleEditRule(rule)}>
                              {rule.name}
                            </button>
                          </td>
                          <td className="d-none d-sm-table-cell">{rule.order}</td>
                          <td>{getAccountName(rule.account)}</td>
                          <td className="d-none d-sm-table-cell">
                            <div className="form-check form-switch mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={rule.enabled}
                                onChange={() => handleToggleRuleEnabled(rule)}
                                disabled={!canChangeRule}
                                title={canChangeRule ? undefined : t('you_dont_have_permissions_to_do_that')}
                              />
                              <label className="form-check-label">
                                <code>{rule.enabled ? t('enabled') : <span className="text-muted">{t('disabled')}</span>}</code>
                              </label>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">
                            <IfPermissions action={PermissionAction.View} type={PermissionType.ProcessedMail}>
                              <Button variant="outline-secondary" size="sm" onClick={() => handleViewProcessedMail(rule)}>
                                <i className="bi bi-clock-history me-1"></i>
                                {t('view_processed_mail')}
                              </Button>
                            </IfPermissions>
                          </td>
                          <td className="text-end">
                            <div className="btn-group">
                              <Button variant="outline-secondary" size="sm" onClick={() => handleEditRule(rule)} title={t('edit')}>
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleEditPermissions(rule)}
                                title={t('permissions')}
                              >
                                <i className="bi bi-person-lock"></i>
                              </Button>
                              <IfPermissions action={PermissionAction.Delete} type={PermissionType.MailRule}>
                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRule(rule)} title={t('delete')}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </IfPermissions>
                              <IfPermissions action={PermissionAction.Add} type={PermissionType.MailRule}>
                                <Button variant="outline-secondary" size="sm" onClick={() => handleCopyRule(rule)} title={t('copy')}>
                                  <i className="bi bi-files"></i>
                                </Button>
                              </IfPermissions>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">{rulesCount > 0 ? `${rulesRangeStart} - ${rulesRangeEnd} / ${rulesCount}` : '0 / 0'}</div>
              <Pagination currentPage={rulesPage} totalPages={rulesTotalPages} onPageChange={setRulesPage} alwaysVisible />
            </div>
          </div>
        </Tab>
        )}
      </Tabs>

      <MailAccountEditDialog
        show={showAccountDialog}
        onHide={handleAccountDialogClose}
        account={editingAccount}
        onSave={handleAccountSave}
        readOnly={!!editingAccount && !canMutateMailAccount(editingAccount)}
      />

      <MailRuleEditDialog
        show={showRuleDialog}
        onHide={handleRuleDialogClose}
        rule={editingRule}
        onSave={handleRuleSave}
        readOnly={!!editingRule && !canMutateMailRule(editingRule)}
      />

      <ProcessedMailDialog show={showProcessedMailDialog} onHide={handleProcessedMailDialogClose} rule={selectedRule} />

      <PermissionsDialog
        show={showPermissionsDialog}
        onHide={() => setShowPermissionsDialog(false)}
        object={selectedObject}
        onSave={handlePermissionsSave}
        readOnly={
          !!selectedObject &&
          ('account' in selectedObject ? !canMutateMailRule(selectedObject) : !canMutateMailAccount(selectedObject))
        }
      />

      <ConfirmDialog
        show={showConfirmDialog}
        title={t('confirm')}
        message={confirmDialogMessage}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction()
          }
        }}
        onCancel={() => setShowConfirmDialog(false)}
        variant="danger"
      />
      <Phase2NoticeModal show={showTotpPhase2Modal} onClose={() => setShowTotpPhase2Modal(false)} />
    </div>
  )
}
